import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, UsersIcon, PackageIcon, AlertCircleIcon, DatabaseIcon, SparklesIcon, ArrowUpIcon, ArrowDownIcon, ActivityIcon, BarChart3Icon } from 'lucide-react';
import { seedData } from '../data/seedData';
import { calculateAverageHealth, countSpacesByVisibility, countAccountsByHealth, countServicesUsage, getTotalSpacesCount, generateActivityLog } from '../lib/analytics';
export function Analytics() {
  const accounts = seedData.accounts;
  // Calculate metrics
  const avgHealth = calculateAverageHealth(accounts);
  const spacesByVisibility = countSpacesByVisibility(accounts);
  const accountsByHealth = countAccountsByHealth(accounts);
  const servicesUsage = countServicesUsage(accounts);
  const totalSpaces = getTotalSpacesCount(accounts);
  const activityLog = generateActivityLog();
  // Calculate trends (simulated)
  const healthTrend = +5.2;
  const spacesGrowth = +12;
  const accountsGrowth = +3;
  return <div className="space-y-8" data-id="element-1751">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-1752">
        <div className="absolute inset-0 -z-10" data-id="element-1753">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/5 rounded-full blur-3xl" data-id="element-1754" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-1755" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-slate-800/80 rounded-3xl p-10 shadow-2xl shadow-black/40 backdrop-blur-xl" data-id="element-1756">
          <div className="flex items-start justify-between" data-id="element-1757">
            <div data-id="element-1758">
              <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white via-green-50 to-slate-200 bg-clip-text text-transparent" data-id="element-1759">
                Analytics & Insights
              </h1>
              <p className="text-slate-400 text-lg flex items-center gap-2" data-id="element-1760">
                <BarChart3Icon className="w-5 h-5 text-green-400" data-id="element-1761" />
                Real-time performance metrics across your portfolio
              </p>
            </div>
            <div className="px-5 py-2.5 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2.5 shadow-lg shadow-green-500/10" data-id="element-1762">
              <ActivityIcon className="w-4 h-4 text-green-400" data-id="element-1763" />
              <span className="text-sm font-bold text-green-400" data-id="element-1764">
                Live Data
              </span>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1765">
        {/* Average Health Score */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1766">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1767" />
          <div className="relative" data-id="element-1768">
            <div className="flex items-center justify-between mb-4" data-id="element-1769">
              <div className="p-3 rounded-xl bg-green-500/10 group-hover:bg-green-500/20 transition-colors" data-id="element-1770">
                <TrendingUpIcon className="w-6 h-6 text-green-400" data-id="element-1771" />
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-green-400" data-id="element-1772">
                <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1773" />
                {healthTrend}%
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1774">
              Avg Health Score
            </p>
            <p className="text-4xl font-bold text-white mb-1" data-id="element-1775">{avgHealth}</p>
            <p className="text-xs text-slate-500" data-id="element-1776">Out of 100</p>
          </div>
        </motion.div>
        {/* Total Accounts */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1777">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1778" />
          <div className="relative" data-id="element-1779">
            <div className="flex items-center justify-between mb-4" data-id="element-1780">
              <div className="p-3 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors" data-id="element-1781">
                <UsersIcon className="w-6 h-6 text-blue-400" data-id="element-1782" />
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-blue-400" data-id="element-1783">
                <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1784" />
                {accountsGrowth}
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1785">
              Active Accounts
            </p>
            <p className="text-4xl font-bold text-white mb-1" data-id="element-1786">
              {accounts.length}
            </p>
            <p className="text-xs text-slate-500" data-id="element-1787">Client relationships</p>
          </div>
        </motion.div>
        {/* Knowledge Spaces */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1788">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1789" />
          <div className="relative" data-id="element-1790">
            <div className="flex items-center justify-between mb-4" data-id="element-1791">
              <div className="p-3 rounded-xl bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors" data-id="element-1792">
                <DatabaseIcon className="w-6 h-6 text-purple-400" data-id="element-1793" />
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-purple-400" data-id="element-1794">
                <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1795" />
                {spacesGrowth}
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1796">
              Knowledge Spaces
            </p>
            <p className="text-4xl font-bold text-white mb-1" data-id="element-1797">{totalSpaces}</p>
            <p className="text-xs text-slate-500" data-id="element-1798">
              {spacesByVisibility.Internal} Internal •{' '}
              {spacesByVisibility.Partner} Partner
            </p>
          </div>
        </motion.div>
        {/* Active Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1799">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1800" />
          <div className="relative" data-id="element-1801">
            <div className="flex items-center justify-between mb-4" data-id="element-1802">
              <div className="p-3 rounded-xl bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors" data-id="element-1803">
                <PackageIcon className="w-6 h-6 text-amber-400" data-id="element-1804" />
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500" data-id="element-1805">
                <ArrowUpIcon className="w-3.5 h-3.5" data-id="element-1806" />0
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1807">
              Active Services
            </p>
            <p className="text-4xl font-bold text-white mb-1" data-id="element-1808">
              {servicesUsage.length}
            </p>
            <p className="text-xs text-slate-500" data-id="element-1809">Product offerings</p>
          </div>
        </motion.div>
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1810">
        {/* Account Health Distribution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-8 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1811">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1812" />
          <div className="relative" data-id="element-1813">
            <div className="flex items-center gap-3 mb-6" data-id="element-1814">
              <div className="p-2 rounded-lg bg-green-500/10" data-id="element-1815">
                <TrendingUpIcon className="w-5 h-5 text-green-400" data-id="element-1816" />
              </div>
              <h3 className="text-lg font-bold text-white uppercase tracking-wider" data-id="element-1817">
                Account Health Distribution
              </h3>
            </div>
            <div className="space-y-4" data-id="element-1818">
              {accountsByHealth.map(item => <div key={item.name} className="space-y-2" data-id="element-1819">
                  <div className="flex items-center justify-between" data-id="element-1820">
                    <span className="text-sm font-semibold text-slate-300" data-id="element-1821">
                      {item.name}
                    </span>
                    <span className="text-sm font-bold text-white" data-id="element-1822">
                      {item.value} accounts
                    </span>
                  </div>
                  <div className="h-3 bg-slate-950/60 rounded-full overflow-hidden border border-slate-800/60" data-id="element-1823">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${item.value / accounts.length * 100}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5
                }} className="h-full rounded-full" style={{
                  backgroundColor: item.color
                }} data-id="element-1824" />
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-1825">
                    {(item.value / accounts.length * 100).toFixed(1)}% of
                    total accounts
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>
        {/* Top Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-8 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1826">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1827" />
          <div className="relative" data-id="element-1828">
            <div className="flex items-center gap-3 mb-6" data-id="element-1829">
              <div className="p-2 rounded-lg bg-blue-500/10" data-id="element-1830">
                <PackageIcon className="w-5 h-5 text-blue-400" data-id="element-1831" />
              </div>
              <h3 className="text-lg font-bold text-white uppercase tracking-wider" data-id="element-1832">
                Top Services by Usage
              </h3>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-1833">
              {servicesUsage.slice(0, 10).map((item, index) => <div key={item.service} className="flex items-center gap-4 p-4 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all group/item" data-id="element-1834">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-lg flex-shrink-0" data-id="element-1835">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1836">
                    <p className="text-sm font-semibold text-white truncate group-hover/item:text-blue-400 transition-colors" data-id="element-1837">
                      {item.service}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1838">
                      {item.count} accounts
                    </p>
                  </div>
                  <div className="flex-shrink-0" data-id="element-1839">
                    <div className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-id="element-1840">
                      <span className="text-xs font-bold text-blue-400" data-id="element-1841">
                        {item.count}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
      {/* Activity Log */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-8 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-1842">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-1843" />
        <div className="relative" data-id="element-1844">
          <div className="flex items-center gap-3 mb-6" data-id="element-1845">
            <div className="p-2 rounded-lg bg-purple-500/10" data-id="element-1846">
              <ActivityIcon className="w-5 h-5 text-purple-400" data-id="element-1847" />
            </div>
            <h3 className="text-lg font-bold text-white uppercase tracking-wider" data-id="element-1848">
              Recent Activity
            </h3>
          </div>
          <div className="space-y-3" data-id="element-1849">
            {activityLog.map(activity => <div key={activity.id} className="flex items-start gap-4 p-5 bg-slate-950/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all group/activity" data-id="element-1850">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${activity.category === 'Sync' ? 'bg-blue-500/10 group-hover/activity:bg-blue-500/20' : activity.category === 'Update' ? 'bg-green-500/10 group-hover/activity:bg-green-500/20' : 'bg-red-500/10 group-hover/activity:bg-red-500/20'}`} data-id="element-1851">
                  {activity.category === 'Sync' ? <DatabaseIcon className={`w-5 h-5 ${activity.category === 'Sync' ? 'text-blue-400' : activity.category === 'Update' ? 'text-green-400' : 'text-red-400'}`} data-id="element-1852" /> : activity.category === 'Update' ? <SparklesIcon className="w-5 h-5 text-green-400" data-id="element-1853" /> : <AlertCircleIcon className="w-5 h-5 text-red-400" data-id="element-1854" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1855">
                  <p className="text-sm font-medium text-white mb-1 group-hover/activity:text-blue-400 transition-colors" data-id="element-1856">
                    {activity.event}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-1857">
                    {new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
                <div className={`px-3 py-1.5 rounded-lg text-xs font-bold border flex-shrink-0 ${activity.category === 'Sync' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : activity.category === 'Update' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`} data-id="element-1858">
                  {activity.category}
                </div>
              </div>)}
          </div>
        </div>
      </motion.div>
    </div>;
}